import { em, px } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import breakpoints from "@/theme/breakpoints.json";

export type BreakpointName = keyof typeof breakpoints;

/**
 * True while the viewport is narrower than the given theme breakpoint. Uses the same boundary as
 * Mantine's `hiddenFrom`, so JS checks and responsive props never both match at one width.
 */
export function useIsBelowBreakpoint(breakpoint: BreakpointName): boolean {
  return useMediaQuery(belowBreakpointQuery(breakpoint), undefined, {
    getInitialValueInEffect: false,
  });
}

/** The media query for widths below a theme breakpoint, ending where `hiddenFrom` starts. */
function belowBreakpointQuery(breakpoint: BreakpointName): string {
  return `(max-width: ${em(Number(px(breakpoints[breakpoint])) - 0.1)})`;
}

/** True on phone-sized viewports: narrower than the theme `sm` breakpoint. */
export function useIsMobile(): boolean {
  return useIsBelowBreakpoint("sm");
}

/** A touch screen this short is a phone held sideways; tablets are taller either way. */
const LANDSCAPE_PHONE_MAX_HEIGHT = 500;

/**
 * True on phones held either way: narrower than `sm`, or a touch screen too short for a tablet
 * layout. A phone in landscape is wider than `sm` but leaves little room under the header.
 */
export function useIsPhone(): boolean {
  const narrow = belowBreakpointQuery("sm");
  const landscape = `(pointer: coarse) and (max-height: ${em(LANDSCAPE_PHONE_MAX_HEIGHT)})`;
  return useMediaQuery(`${narrow}, ${landscape}`, undefined, { getInitialValueInEffect: false });
}
